import { Alert, App, Button, Form, Input, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../api/client';
import { formatPhoneNational, memberKeys, membersApi, type MemberProfile360 } from './api';

interface EraseForm {
  reason: string;
  confirmCode: string;
}

/**
 * Xóa tài khoản khách theo yêu cầu ngoài app (hotline, Zalo, email) — `S4-BE-22`.
 *
 * Xác minh chính chủ là việc của admin, nên bắt ghi lý do (kênh yêu cầu, cách đã xác minh) và gõ lại
 * mã thành viên: một cú bấm nhầm là mất tài khoản không lấy lại được.
 */
export function EraseMemberModal({ member, onClose }: { member: MemberProfile360; onClose: () => void }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EraseForm>();

  const mutation = useMutation({
    mutationFn: (reason: string) => membersApi.erase(member.id, reason),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: memberKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['privacy-requests'] }),
      ]);
      message.success(`Đã xóa tài khoản ${member.memberCode}.`);
      onClose();
    },
    onError: (error) =>
      message.error(
        error instanceof ApiError || error instanceof NetworkError ? error.message : 'Không xóa được tài khoản.',
      ),
  });

  return (
    <Modal
      title="Xóa tài khoản khách"
      open
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={!mutation.isPending}
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="Không hoàn tác được"
        description="Tên, số điện thoại, ngày sinh và thiết bị của khách bị xóa; điểm và voucher chưa dùng mất theo. Lịch sử điểm và hóa đơn được giữ ở dạng ẩn danh."
      />
      <Typography.Paragraph type="secondary">
        Chỉ xóa khi đã chắc người yêu cầu là chủ số {formatPhoneNational(member.phone)}, ví dụ khách gọi hoặc nhắn
        Zalo từ chính số này.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => mutation.mutate(v.reason.trim())}>
        <Form.Item
          name="reason"
          label="Lý do và cách đã xác minh"
          rules={[
            { required: true, whitespace: true, message: 'Ghi kênh khách yêu cầu và cách đã xác minh.' },
            { max: 500, message: 'Tối đa 500 ký tự.' },
          ]}
        >
          <Input.TextArea rows={3} placeholder="Khách gọi hotline từ số đã đăng ký, yêu cầu xóa tài khoản" />
        </Form.Item>
        <Form.Item
          name="confirmCode"
          label={`Gõ lại mã thành viên ${member.memberCode} để xác nhận`}
          rules={[
            { required: true, message: 'Gõ lại mã thành viên.' },
            {
              validator: (_, value: string | undefined) =>
                (value ?? '').trim().toUpperCase() === member.memberCode.toUpperCase()
                  ? Promise.resolve()
                  : Promise.reject(new Error('Mã thành viên không khớp.')),
            },
          ]}
        >
          <Input autoComplete="off" aria-label="Mã thành viên xác nhận" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button danger type="primary" htmlType="submit" loading={mutation.isPending}>
            Xóa tài khoản
          </Button>
          <Button type="link" onClick={onClose} disabled={mutation.isPending}>
            Hủy
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
